import React, { useEffect, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Modal } from 'react-bootstrap';

import camelCaseRecursive from 'camelcase-keys-recursive';
import { FormikInput, FormikSelect } from '../../components';
import { debounceIndexBalancesRequest } from '../../requests/balances';
import { debounceIndexJobManagementsRequest } from '../../requests/jobManagements';
import { debounceIndexBranchOfficesRequest } from '../../requests/branchOffices';
import { debounceIndexCostCentersRequest } from '../../requests/costCenters';
import { indexAdvanceSettingsRequest } from '../../requests/advanceSettings';

const ALL_SELECTED_LABEL = 'Todos';

const options = [
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' }
];

const imputationOption = [
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' }
];

const BookkeeperForm = props => {
  const {
    onHide,
    submitVariant,
    errors,
    touched,
    action,
    setFieldValue,
    setFieldTouched,
    bookkeeper,
    isSubmitting,
    values
  } = props;

  const dispatch = useDispatch();
  const btnMessage = action === 'new' ? 'Crear' : 'Guardar';
  const [balances, setBalances] = useState([]);
  const [selectOptions, setSelectOptions] = useState([]);
  const [defaultRecordId, setDefaultRecordId] = useState();

  const resultFetchBalanceData = response => {
    const { data } = response.data;
    const tempArray = data.map(element => {
      return {
        label: `${element.code} - ${element.name}`,
        value: element.id
      };
    });
    setBalances(tempArray);
  };

  const fetchBalances = () => {
    debounceIndexBalancesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'code',
        paginate: false,
        inactive_exceptions: true
      },
      successCallback: response => {
        resultFetchBalanceData(response);
      }
    });
  };

  const fetchJobManagments = useCallback(() => {
    const resultFetchData = response => {
      setDefaultRecordId(values.bookkeeper.recordId);
      const { data } = response.data;
      const tempArray = data.map(element => {
        return {
          label: element.name,
          value: element.id
        };
      });
      return tempArray;
    };

    debounceIndexJobManagementsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        setSelectOptions(resultFetchData(data));
      }
    });
  }, [dispatch, values.bookkeeper.recordId]);

  const fetchBranchOffices = useCallback(() => {
    const resultFetchData = response => {
      setDefaultRecordId(values.bookkeeper.recordId);
      const { data } = response.data;
      const tempArray = data.map(element => {
        return {
          label: element.name,
          value: element.id
        };
      });
      return tempArray;
    };

    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        setSelectOptions(resultFetchData(data));
      }
    });
  }, [dispatch, values.bookkeeper.recordId]);

  const fetchCostCenters = useCallback(() => {
    const resultFetchData = response => {
      setDefaultRecordId(values.bookkeeper.recordId);
      const { data } = response.data;
      const tempArray = data.map(element => {
        return {
          label: element.name,
          value: element.id
        };
      });
      return tempArray;
    };

    debounceIndexCostCentersRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        setSelectOptions(resultFetchData(data));
      }
    });
  }, [dispatch, values.bookkeeper.recordId]);

  const handleConditionResource = condition => {
    switch (condition) {
      case 'job_management':
        fetchJobManagments();
        break;
      case 'branch_office':
        fetchBranchOffices();
        break;
      case 'cost_center':
        fetchCostCenters();
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
        break;
    }
  };

  const setConditionResource = settingOptions => {
    const settIndex = settingOptions.findIndex(value => value.code === 'condition_for_bookkeeper_association');
    const conditionResource = settIndex > 0 ? settingOptions[settIndex]?.textValue : '';
    handleConditionResource(conditionResource);
  };

  const settingIndex = () => {
    indexAdvanceSettingsRequest({
      dispatch,
      successCallback: response => {
        setConditionResource(camelCaseRecursive(response.data));
      }
    });
  };

  useEffect(fetchBalances, []);
  useEffect(settingIndex, []);

  return (
    <Form>
      <Modal.Body>
        <Row>
          <Col md={5}>
            <Field name="bookkeeper[balanceId]">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  abbr
                  label="Concepto"
                  placeholder="Seleccionar Concepto"
                  options={balances}
                  defaultValue={values.bookkeeper.balanceId}
                  onChange={data => setFieldValue(field.name, data ? data.value : '')}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={5}>
            <Field name="bookkeeper[account]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Cuenta"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={2}>
            <Field name="bookkeeper[code]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Código"
                  defaultValue={values.bookkeeper.code}
                  inputType="text"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  disabled={action === 'edit'}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row>
          <Col md={5}>
            <Field name="bookkeeper[majorAccount]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  label="Cuenta Mayor"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          {selectOptions.length > 0 && (
            <Col md={5}>
              <Field name="bookkeeper[recordId]">
                {({ field }) => (
                  <FormikSelect
                    {...field}
                    isClearable
                    label="Condición"
                    placeholder="Seleccionar Condición"
                    options={selectOptions}
                    defaultValue={defaultRecordId}
                    onChange={data => setFieldValue(field.name, data ? data.value : '')}
                    setFieldTouched={() => setFieldTouched(field.name)}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                )}
              </Field>
            </Col>
          )}
        </Row>
        <Row>
          <Col md={4}>
            <Field name="bookkeeper[parsedDistribution]">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  abbr
                  label="Distribución"
                  placeholder="Seleccionar"
                  options={options}
                  defaultValue={values.bookkeeper.parsedDistribution}
                  onChange={data => {
                    setFieldValue(field.name, data ? data.value : '');
                    setFieldValue('bookkeeper[distribution]', data ? data.value === 'true' : '');
                  }}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="bookkeeper[parsedGrouped]">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  abbr
                  label="Agrupado"
                  placeholder="Seleccionar"
                  options={options}
                  defaultValue={values.bookkeeper.parsedGrouped}
                  onChange={data => {
                    setFieldValue(field.name, data ? data.value : '');
                    setFieldValue('bookkeeper[grouped]', data ? data.value === 'true' : '');
                  }}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="bookkeeper[imputation]">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  abbr
                  label="Imputación"
                  placeholder="Seleccionar"
                  options={imputationOption}
                  defaultValue={bookkeeper.imputation}
                  onChange={data => setFieldValue(field.name, data ? data.value : '')}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="submit" className="btn" variant={submitVariant} disabled={isSubmitting} onClick={onHide}>
          {btnMessage}
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const setInitialValues = props => {
  const allSelected = props.bookkeeper.allCostCenters
    ? [{ label: ALL_SELECTED_LABEL, value: ALL_SELECTED_LABEL }]
    : props.bookkeeper.costCenters;
  const {
    id,
    name,
    code,
    account,
    typeAccount,
    bookkeeperDocumentTypeId,
    costCenter,
    auxiliary,
    analyticsAccount,
    active,
    costCenterIds = props.bookkeeper?.costCenters?.map(cc => cc.id) || [],
    allCostCenters,
    balanceId,
    majorAccount,
    distribution,
    grouped,
    imputation,
    parsedDistribution,
    parsedGrouped,
    parsedImputation,
    translatedDistribution,
    translatedGrouped,
    translatedImputation,
    recordType,
    recordId
  } = props.bookkeeper;
  return {
    bookkeeper: {
      id,
      name,
      code,
      account,
      typeAccount,
      bookkeeperDocumentTypeId,
      costCenter,
      auxiliary,
      analyticsAccount,
      active,
      costCenters: allSelected,
      costCenterIds,
      allCostCenters,
      balanceId,
      majorAccount,
      distribution,
      grouped,
      imputation,
      parsedDistribution,
      parsedGrouped,
      parsedImputation,
      translatedDistribution,
      translatedGrouped,
      translatedImputation,
      recordType,
      recordId
    }
  };
};

const validationSchema = Yup.object().shape({
  bookkeeper: Yup.object().shape({
    name: Yup.string()
      .required('Debes ingresar un nombre')
      .nullable()
      .max(60, 'Deben ser menos que 60 caracteres'),
    code: Yup.string()
      .required('Debes ingresar un código')
      .nullable()
      .max(6, 'Deben ser menos que 6 caracteres'),
    account: Yup.string()
      .required('Debes ingresar la cuenta ')
      .nullable()
      .max(20, 'Deben ser menos que 20 caracteres')
      .alphanumeric('Deben ser caracteres alfanuméricos'),
    majorAccount: Yup.string()
      .nullable()
      .max(100, 'La cuenta mayor no debe superar los 100 caracteres')
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  const newValues = { ...values };
  newValues.bookkeeper.costCenterIds = values.bookkeeper.costCenterIds.filter(cc => cc !== ALL_SELECTED_LABEL);
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(connect()(BookkeeperForm));
